import {
  Box,
  Center,
  Heading,
  Icon,
  Link,
  Stack,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FiLayers } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSub } from "../../common/subscription/useSub";
import {
  FrameworkAgreementStatus,
  PanelStatus,
  PortalFrameworkAgreementDto,
  useGetPortalFrameworkAgreementsQuery,
} from "../../autogen/bff-api";
import { EntityViewSkeleton } from "../../common/EntityViewSkeleton";
import { useApiError } from "../../common/errors/useApiError";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { urls } from "../../urls";

export const SupplierFrameworkAgreementsList = () => {
  const { t } = useTranslation();
  const { hasBuyerAccess } = useSub();
  const displayDate = useDisplayDate();
  const displayer = useApiError();
  const { data, error, isLoading } = useGetPortalFrameworkAgreementsQuery({});

  if (error) return displayer.show(error);
  if (isLoading || !data) return <EntityViewSkeleton />;

  const frameworkAgreements = data.frameworkAgreements ?? [];

  return (
    <Box>
      <Heading size="lg" mb="6">
        {hasBuyerAccess ? t("Framework agreements as supplier") : t("My framework agreements")}
      </Heading>
      {frameworkAgreements.length === 0 ? (
        <Center
          py="16"
          px="6"
          bg="smBackground"
          rounded="xl"
          border="1px solid"
          borderColor="smBorder"
          flexDirection="column"
          gap="3"
          color="smMuted"
        >
          <Icon as={FiLayers} boxSize="28px" />
          <Text fontWeight="medium" color="smText">
            {t("You are not a supplier on any framework agreements yet")}
          </Text>
          <Text fontSize="sm" maxW="440px" textAlign="center">
            {t("You join one when a buyer awards you a place on their panel.")}
          </Text>
        </Center>
      ) : (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t("Framework agreement")}</Th>
                  <Th>{t("Buyer")}</Th>
                  <Th>{t("Ranking")}</Th>
                  <Th>{t("Status")}</Th>
                  <Th>{t("Expires")}</Th>
                  <Th>{t("Call-offs")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {frameworkAgreements.map((framework) => (
                  <Tr key={framework.id}>
                    <Td fontWeight="semibold">{framework.title}</Td>
                    <Td>{framework.buyerOrganizationName}</Td>
                    <Td>{framework.ranking ?? "-"}</Td>
                    <Td>
                      <StatusTag framework={framework} />
                    </Td>
                    <Td>{framework.endDate ? displayDate(framework.endDate) : "-"}</Td>
                    <Td>
                      <CallOffLinks framework={framework} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

const CallOffLinks = ({ framework }: { framework: PortalFrameworkAgreementDto }) => {
  const callOffs = framework.callOffs ?? [];
  if (callOffs.length === 0) return <Text color="smMuted">-</Text>;
  return (
    <Stack spacing="1">
      {callOffs.map((callOff) => (
        <Link
          key={callOff.contractId}
          as={RouterLink}
          to={urls.app.contracts.view.go(callOff.contractId)}
          color="smPrimary"
        >
          {callOff.title}
        </Link>
      ))}
    </Stack>
  );
};

const StatusTag = ({ framework }: { framework: PortalFrameworkAgreementDto }) => {
  const { t } = useTranslation();
  if (framework.status === FrameworkAgreementStatus.Terminated) {
    return (
      <Tag size="sm" colorScheme="gray">
        {t(framework.status)}
      </Tag>
    );
  }
  return (
    <Tag size="sm" colorScheme={framework.panelStatus === PanelStatus.Active ? "teal" : "orange"}>
      {t(framework.panelStatus)}
    </Tag>
  );
};
